import React, { FC, useEffect, useState } from "react"
import {
  Button,
  Col,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Typography,
} from "antd"
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons"
import Slider from "rc-slider"
import "rc-slider/assets/index.css"
import { AbExperiment, AbVariantFormValue, PayloadDiscriminatorValue, SetExperimentFormValues } from "../../store/types"
import { AccountSelect } from "../account-select"
import { EXPERIMENT_PAYLOAD_CONFIGS, KNOWN_EXPERIMENTS } from "./experiment-configs"
import { SEGMENT_COLORS, cutoffsToHandles, evenHandles, handlesToPercentages } from "./distribution-utils"
import {
  buildPayload,
  getBooleanFromPayload,
  getDiscriminatorFromPayload,
  getMultiSelectFromPayload,
  getOrderedListFromPayload,
  getParamsFromPayload,
  getSurveyFromPayload,
} from "./payload-utils"
import { OrderedListEditor } from "./ordered-list-editor"
import { SurveyQuestionsEditor } from "./survey-questions-editor"
import { DistributionBar } from "./distribution-bar"

interface Props {
  visible: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    variants: Array<{
      name: string;
      upperCutoffPercentage: number;
      forceOnAccountIds: string[];
      forceOffAccountIds: string[];
      payload?: Record<string, unknown>;
    }>;
  }) => Promise<void>;
  initialExperiment?: AbExperiment | null;
}

export const SetExperimentModal: FC<Props> = ({
  visible,
  onClose,
  onSubmit,
  initialExperiment,
}) => {
  const [form] = Form.useForm<SetExperimentFormValues>()
  const [handles, setHandles] = useState<number[]>([])
  const [experimentName, setExperimentName] = useState<string>("")
  const [discriminators, setDiscriminators] = useState<string[]>([])

  const variantNames: string[] = (
    (form.getFieldValue("variants") as AbVariantFormValue[] | undefined) ?? []
  ).map(v => v?.name || "")

  const percentages = handlesToPercentages(handles, handles.length + 1)
  const payloadConfig = EXPERIMENT_PAYLOAD_CONFIGS[experimentName]
  const defaultDiscriminator = payloadConfig ? Object.keys(payloadConfig.values || {})[0] : ""

  useEffect(() => {
    if (!visible) return

    if (initialExperiment) {
      const config = EXPERIMENT_PAYLOAD_CONFIGS[initialExperiment.name]
      const cutoffs = initialExperiment.variants.map(v => v.upperCutoffPercentage)

      setHandles(cutoffsToHandles(cutoffs))
      setExperimentName(initialExperiment.name)
      setDiscriminators(
        initialExperiment.variants.map(v => config ? getDiscriminatorFromPayload(v.payload, config) : ""),
      )

      form.setFieldsValue({
        name: initialExperiment.name,
        variants: initialExperiment.variants.map(v => ({
          name: v.name,
          forceOnAccountIds: v.forceOnAccountIds.map(id => ({ value: id, label: id })),
          forceOffAccountIds: v.forceOffAccountIds.map(id => ({ value: id, label: id })),
          payloadDiscriminator: config?.discriminator ? getDiscriminatorFromPayload(v.payload, config) : undefined,
          payloadParams: config?.discriminator ? getParamsFromPayload(v.payload, config) : undefined,
          payloadOrderedList: config?.orderedList ? getOrderedListFromPayload(v.payload, config) : undefined,
          payloadBoolean: config?.booleanField ? getBooleanFromPayload(v.payload, config) : undefined,
          payloadSurvey: config?.surveyQuestions ? getSurveyFromPayload(v.payload, config) : undefined,
          payloadMultiSelect: config?.multiSelect ? getMultiSelectFromPayload(v.payload, config) : undefined,
        })),
      })
    } else {
      form.resetFields()
      setHandles([])
      setExperimentName("")
      setDiscriminators([""])
    }
  }, [visible, initialExperiment, form])

  const handleOk = async() => {
    const values = await form.validateFields()
    const count = values.variants.length
    const percs = handlesToPercentages(handles, count)

    let cumulative = 0
    const variants = values.variants.map((v, i) => {
      cumulative += percs[i] / 100
      const upperCutoffPercentage = i === count - 1 ? 1 : parseFloat(cumulative.toFixed(4))

      return {
        name: v.name,
        upperCutoffPercentage,
        forceOnAccountIds: (v.forceOnAccountIds || []).map(o => o.value),
        forceOffAccountIds: (v.forceOffAccountIds || []).map(o => o.value),
        payload: buildPayload(values.name, v),
      }
    })

    await onSubmit({ name: values.name, variants })
    onClose()
  }

  return (
    <Modal
      title="Set Experiment"
      open={visible}
      onCancel={onClose}
      onOk={handleOk}
      width={740}
      okText="Set Experiment"
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          variants: [
            {
              name: "",
              forceOnAccountIds: [],
              forceOffAccountIds: [],
              payloadDiscriminator: defaultDiscriminator,
            },
          ],
        }}
      >
        {/* Experiment name */}
        <Form.Item name="name" label="Experiment" rules={[{ required: true, message: "Required" }]}>
          <Select
            showSearch
            options={KNOWN_EXPERIMENTS.map(e => ({ value: e, label: e }))}
            onChange={v => {
              setExperimentName(v)
              const cfg = EXPERIMENT_PAYLOAD_CONFIGS[v]
              const def = cfg ? Object.keys(cfg.values || {})[0] : ""
              const variants: AbVariantFormValue[] = form.getFieldValue("variants") || []

              setDiscriminators(variants.map(() => def))
              form.setFieldsValue({
                variants: variants.map(variant => ({
                  ...variant,
                  payloadDiscriminator: def,
                  payloadParams: undefined,
                })),
              })
            }}
            placeholder="Select experiment"
            allowClear
          />
        </Form.Item>

        {/* Distribution slider */}
        <Form.Item label="Variant Distribution">
          <DistributionBar names={variantNames} percentages={percentages} />
          {handles.length > 0 && (
            <div style={{ padding: "4px 8px 8px" }} id="set-bp2-ab-experiment-modal-slider">
              <style>{`
                #set-bp2-ab-experiment-modal-slider .rc-slider-rail {
                  background: linear-gradient(to right,
                    ${SEGMENT_COLORS[0]} 0%,
                    ${SEGMENT_COLORS[0]} ${handles[0]}%,
                    ${SEGMENT_COLORS[handles.length % SEGMENT_COLORS.length]} ${handles[handles.length - 1]}%,
                    ${SEGMENT_COLORS[handles.length % SEGMENT_COLORS.length]} 100%);
                }
              `}
              </style>
              <Slider
                min={0}
                max={100}
                range
                value={handles}
                onChange={v => setHandles(Array.isArray(v) ? v : [v as number])}
                pushable
                allowCross={false}
                trackStyle={handles.map((_, i) => ({ backgroundColor: SEGMENT_COLORS[(i + 1) % SEGMENT_COLORS.length] }))}
                handleStyle={handles.map(() => ({ borderColor: "#888" }))}
              />
            </div>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
            {percentages.map((pct, i) => (
              <Typography.Text key={i} type="secondary" style={{ fontSize: 12 }}>
                <span style={{ color: SEGMENT_COLORS[i % SEGMENT_COLORS.length] }}>■</span>{" "}
                {variantNames[i] || `Variant ${i + 1}`}: {pct}%
              </Typography.Text>
            ))}
          </div>
        </Form.Item>

        {/* Variants */}
        <Form.List name="variants">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name: fieldName, ...restField }) => {
                const discriminator = discriminators[fieldName] ?? defaultDiscriminator
                const valueConfig = payloadConfig?.values?.[discriminator]

                return (
                  <div
                    key={key}
                    style={{
                      border: "1px solid #d9d9d9",
                      borderLeft: `4px solid ${SEGMENT_COLORS[fieldName % SEGMENT_COLORS.length]}`,
                      borderRadius: 6,
                      padding: "12px 16px",
                      marginBottom: 12,
                    }}
                  >
                    <Space align="start" style={{ width: "100%", justifyContent: "space-between" }}>
                      <Typography.Text strong>
                        Variant {fieldName + 1} · {percentages[fieldName] ?? 0}%
                      </Typography.Text>
                      {fields.length > 1 && (
                        <MinusCircleOutlined
                          style={{ color: "red", cursor: "pointer" }}
                          onClick={() => {
                            remove(fieldName)
                            setHandles(evenHandles(fields.length - 1))
                            setDiscriminators(prev => prev.filter((_, i) => i !== fieldName))
                          }}
                        />
                      )}
                    </Space>

                    <Form.Item
                      {...restField}
                      name={[fieldName, "name"]}
                      label="Variant Name"
                      rules={[{ required: true, message: "Required" }]}
                      style={{ marginTop: 8 }}
                    >
                      <Input placeholder="e.g. control, treatment" />
                    </Form.Item>

                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[fieldName, "forceOnAccountIds"]}
                          label="Force ON accounts"
                        >
                          <AccountSelect placeholder="Search accounts" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[fieldName, "forceOffAccountIds"]}
                          label="Force OFF accounts"
                        >
                          <AccountSelect placeholder="Search accounts" />
                        </Form.Item>
                      </Col>
                    </Row>

                    {payloadConfig?.surveyQuestions && (
                      <Form.Item
                        {...restField}
                        name={[fieldName, "payloadSurvey"]}
                        label="Survey Questions"
                      >
                        <SurveyQuestionsEditor allQuestions={payloadConfig.surveyQuestions.questions} />
                      </Form.Item>
                    )}

                    {payloadConfig?.multiSelect && (
                      <Form.Item
                        {...restField}
                        name={[fieldName, "payloadMultiSelect"]}
                        label={payloadConfig.multiSelect.label}
                        initialValue={payloadConfig.multiSelect.defaultValue}
                      >
                        <Select
                          mode="multiple"
                          allowClear
                          options={payloadConfig.multiSelect.options}
                          placeholder="Select sources"
                        />
                      </Form.Item>
                    )}

                    {payloadConfig?.booleanField && (
                      <Form.Item
                        {...restField}
                        name={[fieldName, "payloadBoolean"]}
                        label={payloadConfig.booleanField.label}
                        initialValue={payloadConfig.booleanField.defaultValue}
                      >
                        <Select
                          options={[
                            { value: true, label: "Yes" },
                            { value: false, label: "No" },
                          ]}
                        />
                      </Form.Item>
                    )}

                    {payloadConfig?.orderedList && (
                      <Form.Item
                        {...restField}
                        name={[fieldName, "payloadOrderedList"]}
                        label={payloadConfig.orderedList.label}
                        initialValue={payloadConfig.orderedList.defaultValue}
                      >
                        <OrderedListEditor options={payloadConfig.orderedList.options} />
                      </Form.Item>
                    )}

                    {payloadConfig?.discriminator && (
                      <Form.Item
                        {...restField}
                        name={[fieldName, "payloadDiscriminator"]}
                        label={payloadConfig.discriminatorLabel}
                      >
                        <Select
                          options={
                            Object.entries(payloadConfig.values ?? {})
                              .map(
                                ([val, cfg]) => ({
                                  value: val,
                                  label: (cfg as PayloadDiscriminatorValue).label,
                                }))
                          }
                          onChange={v => {
                            const next = [...discriminators]

                            next[fieldName] = v
                            setDiscriminators(next)
                          }}
                        />
                      </Form.Item>
                    )}

                    {payloadConfig && valueConfig?.params && (
                      <Collapse ghost>
                        <Collapse.Panel
                          header={`${valueConfig.label} parameters (optional overrides)`}
                          key="params"
                        >
                          <Row gutter={12}>
                            {valueConfig.params.map(({ key: paramKey, label, type: paramType }) => (
                              <Col span={12} key={paramKey}>
                                <Form.Item
                                  {...restField}
                                  name={[fieldName, "payloadParams", paramKey]}
                                  label={label}
                                >
                                  {paramType === "string" ? (
                                    <Input placeholder="Default" />
                                  ) : paramType === "boolean" ? (
                                    <Select
                                      allowClear
                                      placeholder="Default"
                                      options={[
                                        { value: true, label: "Yes" },
                                        { value: false, label: "No" },
                                      ]}
                                    />
                                  ) : (
                                    <InputNumber style={{ width: "100%" }} placeholder="Default" />
                                  )}
                                </Form.Item>
                              </Col>
                            ))}
                          </Row>
                        </Collapse.Panel>
                      </Collapse>
                    )}
                  </div>
                )
              })}

              <Button
                type="dashed"
                block
                icon={<PlusOutlined />}
                onClick={() => {
                  add({
                    name: "",
                    forceOnAccountIds: [],
                    forceOffAccountIds: [],
                    payloadDiscriminator: defaultDiscriminator,
                  })
                  setHandles(evenHandles(fields.length + 1))
                  setDiscriminators(prev => [...prev, defaultDiscriminator])
                }}
              >
                Add Variant
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  )
}
